import React, { useEffect, useState } from 'react'
import OutlinedCloseIcon from '../../svgIcons/OtlinedCloseIcon';
import styles from './AddEvent.module.css'
import { Controller, useForm } from 'react-hook-form';
import TextInput from '../../common/input/textInput/TextInput';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { colourStyles, REGEX } from '../../../constants/constants';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { locationsData } from '../../locations/Locations';
import { useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import moment from 'moment';
import TextArea from '../../common/input/textArea/TextArea';
import { useDispatch } from 'react-redux';
import { getLocation } from '../../../redux/location/actions/location.action';
import { createPropertyEvent, updatePropertyEvent } from '../../../redux/key-pickup-drop-events/actions/key-pickup-drop-events.action';
import { EventsLockerListParams, getEventsLockerList, resetEventsLockerListState } from '../../../redux/events-locker-list/actions/eventsLockerList.action';
import { trimData } from '../../../utils/trim-data';
import toast from 'react-hot-toast';
import Loader from '../../common/loader/Loader';
interface eventProps {
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    setShowToast?: React.Dispatch<React.SetStateAction<boolean>>;
    locationId?: string;
    setToastMessage?: React.Dispatch<React.SetStateAction<string>>;
    setToastType?: React.Dispatch<React.SetStateAction<string>>;
    onSuccess?: () => void;
    onAddSuccess?: (eventData?: EventDataType) => void;
    eventData?: EventDataType;   // <-- for edit
    isEditMode?: boolean;
    onCloseModal?: () => void;
}

interface EventDataType {
    id: string;
    name: string;
    email: string;
    phone: string;
    accessCode: string;
    eventName: string;
    description: string;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    locker: number | null;
    adminId: string;
    locationId: string;
    locationName: string;
}

interface eventField {
    propertyName: string;
    email: string;
    name: string;
    phone: string;
    eventType: string;
    startDate: Date | string | null;
    endDate: Date | string | null;
    startTime: string;
    endTime: string;
    locker: string | null;
    specialNotes: string;
}

interface EventTypeDetails {
    title: string;
    description: string;
}

export enum EventTypes {
    KEY_DROP = "Key Drop",
    KEY_PICKUP = "Key Pickup",
    KEY_DROP_PICKUP = "Key Pickup & Drop",
}

const DISPLAY_DATE_FORMAT = 'MM-DD-YYYY';
const API_DATE_FORMAT = 'YYYY-MM-DD';

const parseEventDate = (value: Date | string | null | undefined) => {
    if (!value) return null;

    if (value instanceof Date) {
        const parsedDate = moment(value);
        return parsedDate.isValid() ? parsedDate : null;
    }

    const parsedDate = moment(value, [DISPLAY_DATE_FORMAT, API_DATE_FORMAT, moment.ISO_8601], true);
    return parsedDate.isValid() ? parsedDate : null;
};

const eventDateToDate = (value: Date | string | null | undefined) => {
    return parseEventDate(value)?.toDate() || null;
};

const formatEventDateForApi = (value: Date | string | null | undefined) => {
    return parseEventDate(value)?.format(API_DATE_FORMAT) || '';
};

const EventTypeDetails: Record<EventTypes, EventTypeDetails> = {
    [EventTypes.KEY_DROP]: {
        title: "Key Drop",
        description: "(Person is leaving a key in a locker)"
    },
    [EventTypes.KEY_PICKUP]: {
        title: "Key Pickup",
        description: "(Person is retrieving a key from locker)"
    },
    [EventTypes.KEY_DROP_PICKUP]: {
        title: "Key Pickup & Drop",
        description: ""
    }
};

export function validatePhone(number: string, countryCode: CountryCode) {
    const phoneNumber = parsePhoneNumberFromString(number, countryCode);
    return phoneNumber?.isValid() || false;
}

interface LockerOption {
    label: string;
    value: string
}

const AddEvent = ({ setOpen, setShowToast, setToastMessage, setToastType, onSuccess, onAddSuccess, locationId, isEditMode, eventData, onCloseModal }: eventProps) => {
    const dispatch: AppDispatch = useDispatch();
    const params = useParams();
    const { id: location_id } = params;
    const allLocationsList = useSelector((state: IRootState) => state.locationReducer.data.locations);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const locationLoading = useSelector((state: IRootState) => state.locationReducer.loading);
    const [locations, setLocations] = useState<locationsData[]>([]);
    const [availableLockers, setAvailableLockers] = useState<LockerOption[]>([]);
    const [phoneNumberLength, setPhoneNumberLength] = useState(11);
    const [missingFieldsMessage, setMissingFieldsMessage] = useState<string>('');
    const [dialCodeLength, setDialCodeLength] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const { data: lockerData, error: lockersError, loading: isLockerLoading } = useSelector((state: IRootState) => state.keyDropEventReducer);
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        watch,
        setValue,
        trigger,
        getValues,
    } = useForm<eventField>({
        defaultValues: {
            propertyName: '',
            name: '',
            email: '',
            phone: '',
            startDate: null,
            endDate: null,
            startTime: '',
            endTime: '',
            locker: '',
            specialNotes: '',
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        if (isEditMode && eventData?.id) {
            // Set initializing to true when starting to populate form in edit mode
            setIsInitializing(true);
            const startDate = eventData.startDate
                ? parseEventDate(eventData.startDate)?.hour(12).minute(0).second(0).toDate() || null
                : null;
            const endDate = eventData.endDate
                ? parseEventDate(eventData.endDate)?.hour(12).minute(0).second(0).toDate() || null
                : null;

            reset({
                propertyName: eventData?.locationName,
                name: eventData.name,
                email: eventData.email,
                phone: eventData.phone,
                eventType: eventData.eventName,
                startDate: startDate,
                endDate: endDate,
                startTime: eventData.startTime,
                endTime: eventData.endTime,
                locker: "",
                specialNotes: eventData.description || '',
            });
        }
    }, [isEditMode]);


    useEffect(() => {
        if (!isLockerLoading) {
            if (lockerData && lockerData?.length) {
                // Sort locker data numerically before creating the array
                const sortedLockerData = [...lockerData].sort((a: string, b: string) => {
                    const numA = parseInt(a, 10);
                    const numB = parseInt(b, 10);
                    return numA - numB;
                });

                const lockersArray = sortedLockerData?.map((locker: string) => ({
                    value: locker?.toString(),
                    label: `Locker ${locker}`
                }));

                setAvailableLockers(lockersArray);
                if (isEditMode && eventData?.locker) {
                    const findLocker = lockersArray?.find((locker: LockerOption) => locker.value?.toString() === eventData?.locker?.toString());
                    if (findLocker) {
                        setValue('locker', findLocker.value);
                    }
                }

                // Mark initialization complete after locker is set in edit mode
                if (isEditMode && isInitializing) {
                    setIsInitializing(false);
                }
            } else {
                setAvailableLockers([]);

                // Also mark initialization complete even if no lockers available
                if (isEditMode && isInitializing) {
                    setIsInitializing(false);
                }
            }
        }
    }, [isLockerLoading, lockerData, isEditMode, eventData?.locker, isInitializing]);

    useEffect(() => {
        dispatch(getLocation())

        return () => {
            reset();
            setValue('propertyName', '');
            setValue('name', '');
            setValue('email', '');
            setValue('phone', '');
            setValue('startDate', null);
            setValue('endDate', null);
            setValue('startTime', '');
            setValue('endTime', '');
            setValue('locker', null);
            setValue('specialNotes', '');
            setAvailableLockers([]);
            setMissingFieldsMessage('');
            setDialCodeLength(0);
            setPhoneNumberLength(11);
            setLoading(false);
            setIsInitializing(false);
            dispatch(resetEventsLockerListState());
        }
    }, [])


    useEffect(() => {
        if (eventData) {
            if (eventData.locationId) {
                setValue("propertyName", eventData.locationId);
            }
        }
    }, [eventData, setValue]);


    useEffect(() => {
        if (location_id && !locationLoading && locations && locations?.length) {
            const property = locations?.find((location: locationsData) => location?.id === location_id);
            if (property && property?.id) {
                setValue('propertyName', property?.id);
            }
        }
    }, [location_id, locations, locationLoading]);

    useEffect(() => {
        if (missingFieldsMessage) {
            setTimeout(() => {
                setMissingFieldsMessage('');
            }, 4000);
        }
    }, [missingFieldsMessage]);

    useEffect(() => {
        if (!invitedLocations?.length && !allLocationsList?.length) {
            setLocations([]);
        } else {
            if (invitedLocations?.length || allLocationsList?.length) {
                const data = invitedLocations?.map((list: locationsData) => {
                    return {
                        ...list,
                        isRestricted: true,
                    };
                });
                setLocations([...data, ...allLocationsList]);
            }
        }
    }, [allLocationsList?.length, invitedLocations?.length]);

    const startDate = watch("startDate");
    const endDate = watch("endDate");
    const startTime = watch("startTime");
    const endTime = watch("endTime");

    // Comprehensive validation function for fetchLockers
    const validateFetchLockersConditions = () => {
        const errors: string[] = [];
        // Check if all required fields have values
        if (!startDate) errors.push("Start date is required");
        if (!endDate) errors.push("End date is required");
        if (!startTime) errors.push("Start time is required");
        if (!endTime) errors.push("End time is required");
        if (!locationId && !watch('propertyName')?.toString()) errors.push("Location ID is required");

        // If any basic required fields are missing, return early
        if (errors.length > 0) {
            console.warn("Missing required fields:", errors.join(", "));
            return { isValid: false, errors };
        }

        // Validate date format first
        if (!parseEventDate(startDate)) {
            errors.push("Invalid start date format");
        }

        if (!parseEventDate(endDate)) {
            errors.push("Invalid end date format");
        }

        // Validate time format
        if (!moment(startTime, "hh:mm a", true).isValid()) {
            errors.push("Invalid start time format");
        }

        if (!moment(endTime, "hh:mm a", true).isValid()) {
            errors.push("Invalid end time format");
        }

        // If format validation fails, return early
        if (errors.length > 0) {
            return { isValid: false, errors };
        }

        // Validate date logic
        const startDateMoment = parseEventDate(startDate)!;
        const endDateMoment = parseEventDate(endDate)!;
        const today = moment().startOf("day");

        // Check if start date is not in the past
        if (startDateMoment.isBefore(today)) {
            errors.push("Start date cannot be in the past");
        }

        // Check if end date is not in the past
        if (endDateMoment.isBefore(today)) {
            errors.push("End date cannot be in the past");
        }

        // Check if end date is after or same as start date
        if (endDateMoment.isBefore(startDateMoment, "day")) {
            errors.push("End date must be same or after start date");
        }

        // 🟢 New validation: if start date is today, start time must be in the future
        if (startDateMoment.isSame(moment(), "day")) {
            const startTimeMoment = moment(startTime, "hh:mm a");
            const now = moment();
            if (startTimeMoment.isSameOrBefore(now, "minute")) {
                errors.push("Start time must be in the future");
            }
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    };


    useEffect(() => {
        dispatch(resetEventsLockerListState());
        setAvailableLockers([]);
        setValue("locker", null);

        const fetchLockers = async () => {
            const validation = validateFetchLockersConditions();
            if (!validation.isValid) {
                dispatch(resetEventsLockerListState());
                setAvailableLockers([]);
                setValue("locker", null);
                return;
            }

            if (startDate && endDate) {
                const start = parseEventDate(startDate)!;
                const end = parseEventDate(endDate)!;
                if (end.isBefore(start, "day")) {
                    console.warn("End date must be the same or after start date");
                    return;
                }
            }

            if (startDate && endDate && startTime && endTime && (locationId || watch('propertyName')?.toString())) {
                try {
                    if ((locationId || watch('propertyName')?.toString())) {
                        const params: EventsLockerListParams = {
                            locationId: locationId || watch('propertyName')?.toString(),
                            startDate: formatEventDateForApi(startDate),
                            endDate: formatEventDateForApi(endDate),
                            startTime: moment(startTime, "hh:mm A").format("hh:mm A"),
                            endTime: moment(endTime, "hh:mm A").format("hh:mm A"),
                            ...(eventData?.id && { propertyEventId: eventData?.id }),
                        };
                        await dispatch(getEventsLockerList(params))
                    }
                } catch (error) {
                    console.error("Failed to fetch lockers", error);
                    setAvailableLockers([]);
                }
            }
        };

        fetchLockers();
    }, [startDate, endDate, startTime, endTime, locationId, dispatch, watch('propertyName')?.toString()]);


    useEffect(() => {
        if (lockersError && setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType("error");
            setToastMessage(lockersError);

            setTimeout(() => {
                setShowToast(false);
                setToastType('');
                setToastMessage('');
            }, 4000000);
        }
    }, [lockersError]);

    useEffect(() => {
        if (startTime) {
            trigger('startTime');
        }
        if (endTime) {
            trigger('endTime');
        }
    }, [startTime, endTime, startDate, endDate, trigger]);


    const closeModal = () => {
        // Reset all form fields
        reset();
        setValue('propertyName', '');
        setValue('name', '');
        setValue('email', '');
        setValue('phone', '');
        setValue('startDate', null);
        setValue('endDate', null);
        setValue('startTime', '');
        setValue('endTime', '');
        setValue('locker', null);
        setValue('specialNotes', '');

        // Reset all states
        setAvailableLockers([]);
        setMissingFieldsMessage('');
        setDialCodeLength(0);
        setPhoneNumberLength(11);
        setLoading(false);
        setIsInitializing(false);

        // Reset locker list state
        dispatch(resetEventsLockerListState());

        // Close the modal
        if (setOpen) setOpen(false);
        onCloseModal?.();
    };

    const handleTrimStartChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        onChange: (value: string) => void
    ) => {
        const trimmedValue = e.target.value.trimStart();
        onChange(trimmedValue);
    };

    // Helper function to check if time slot is valid based on conditions
    const isTimeSlotValid = (time: Date, isStartTime = true) => {
        if (!startDate || !endDate) return true;

        const currentTime = moment(time);
        const startDateMoment = parseEventDate(startDate);
        const endDateMoment = parseEventDate(endDate);
        if (!startDateMoment || !endDateMoment) return true;
        const isSameDay = startDateMoment.isSame(endDateMoment, 'day');

        // For allowEntireTimeBlock
        if (isSameDay) {
            // Same day: need 1 hour difference between start and end time
            if (isStartTime) {
                // For start time, check if there's a valid end time that's at least 1 hour later
                if (endTime) {
                    const endTimeMoment = moment(endTime, 'hh:mm a');
                    const diffInMinutes = endTimeMoment.diff(currentTime, 'minutes');
                    return diffInMinutes >= 60;
                }
            } else {
                // For end time, check if there's a valid start time that's at least 1 hour earlier
                if (startTime) {
                    const startTimeMoment = moment(startTime, 'hh:mm a');
                    const diffInMinutes = currentTime.diff(startTimeMoment, 'minutes');
                    return diffInMinutes >= 60;
                }
            }

            // Dfferent days: no time validation needed
            return true;
        }

        // For allowStratEndTimeEachDay

        if (isStartTime) {
            // For start time, check if there's a valid end time that's at least 1 hour later
            if (endTime) {
                const endTimeMoment = moment(endTime, 'hh:mm a');
                const diffInMinutes = endTimeMoment.diff(currentTime, 'minutes');
                return diffInMinutes >= 60;
            }
        } else {
            // For end time, check if there's a valid start time that's at least 1 hour earlier
            if (startTime) {
                const startTimeMoment = moment(startTime, 'hh:mm a');
                const diffInMinutes = currentTime.diff(startTimeMoment, 'minutes');
                return diffInMinutes >= 60;
            }
        }

        return true;
    }

    const filterTimeSlots = (time: Date) => {
        const selectedDate = watch('startDate') ? parseEventDate(watch('startDate')) : null; // ✅ Block past times if date is today 
        if (selectedDate && selectedDate.isSame(moment(), 'day')) { return moment(time).isAfter(moment(), 'minute'); } return true;
    }

    // Helper function to filter end time slots
    const filterEndTimeSlots = (time: Date) => {
        // Only apply restrictions if end date is today
        const selectedEndDate = watch('endDate') ? parseEventDate(watch('endDate')) : null;
        const isEndDateToday = selectedEndDate && selectedEndDate.isSame(moment(), 'day');

        // If end date is today, block past times
        if (isEndDateToday) {
            return moment(time).isAfter(moment(), 'minute');
        }

        // If end date is in the future (not same as start date), allow all times
        const startDateMoment = parseEventDate(startDate);
        const endDateMoment = parseEventDate(endDate);
        if (!startDateMoment || !endDateMoment) return true;
        const isDifferentDays = !startDateMoment.isSame(endDateMoment, 'day');

        // For different days, no time restrictions needed (allow past slots)
        if (isDifferentDays) {
            return true;
        }

        // For same day, use the existing validation
        return isTimeSlotValid(time, false);
    }

    const createEventSuccess = (message: string, apiEventData?: any) => {
        if (setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType('success');
            setToastMessage(message);
            closeModal();
            onSuccess?.()

            // Convert API response to EventDataType format
            if (apiEventData) {
                const eventData: EventDataType = {
                    id: apiEventData.id || '',
                    name: apiEventData.name || '',
                    email: apiEventData.email || '',
                    phone: apiEventData.phone || '',
                    accessCode: apiEventData.accessCode || '',
                    eventName: apiEventData.eventName || '',
                    description: apiEventData.description || '',
                    startDate: apiEventData.startDate || '',
                    endDate: apiEventData.endDate || '',
                    startTime: apiEventData.startTime || '',
                    endTime: apiEventData.endTime || '',
                    locker: apiEventData.locker || null,
                    adminId: apiEventData.adminId || '',
                    locationId: apiEventData.locationId || '',
                    locationName: apiEventData.locationName || locations.find(loc => loc.id === apiEventData.locationId)?.name || '',
                };
                onAddSuccess?.(eventData);
            } else {
                onAddSuccess?.();
            }
        }
    }

    const createEventFailure = (message: string) => {
        if (setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType('error');
            setToastMessage(message);
        }
    }
    const submitHandler = async (data: eventField) => {
        // Prepare the payload as expected by API
        const trim = (val: string) => trimData<string>(val) || '';
        const startDateTime = data.startDate && data.startTime
            ? moment(`${formatEventDateForApi(data.startDate)} ${data.startTime}`, `${API_DATE_FORMAT} hh:mm a`)
            : null;

        const endDateTime = data.endDate && data.endTime
            ? moment(`${formatEventDateForApi(data.endDate)} ${data.endTime}`, `${API_DATE_FORMAT} hh:mm a`)
            : null;

        if (startDateTime && endDateTime) {
            const diffInMinutes = endDateTime.diff(startDateTime, 'minutes');
            if (diffInMinutes < 60) {
                toast.error('Events time must be at least 1 hour long');
                return;
            }
        }
        const phoneDigits = data.phone?.replace(/\D/g, '') || '';
        const actualPhone =
            phoneDigits.length > (dialCodeLength || 0)
                ? data.phone
                : '';

        const payload = {
            locationId: data.propertyName,
            name: trim(data.name),
            email: data.email,
            phone: actualPhone,
            eventName: data.eventType, // or map id to name if needed
            description: trim(data.specialNotes),
            startDate: formatEventDateForApi(data.startDate),
            endDate: formatEventDateForApi(data.endDate),
            startTime: data.startTime ? moment(data.startTime, 'hh:mm a').format('hh:mm A') : '',
            endTime: data.endTime ? moment(data.endTime, 'hh:mm a').format('hh:mm A') : '',
            locker: Number(data.locker),
        };
       
        setLoading(true);
        if (isEditMode && eventData?.id) {
            await dispatch(updatePropertyEvent(eventData.id, payload, setLoading, createEventSuccess, createEventFailure));
        } else {
            await dispatch(createPropertyEvent(payload, setLoading, createEventSuccess, createEventFailure));
        }
    };

    return (
        <div className={styles.modalAddStaff}>
            {(loading || (isEditMode && isInitializing)) && <Loader />}
            <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>
                    <h4>{isEditMode ? 'Update ' : 'Create '} Pickup & Drop Event</h4>
                </div>
                <div className={styles.close} onClick={closeModal}>
                    <OutlinedCloseIcon />
                </div>
            </div>

            <div className={styles.modalBody}>
                <form onSubmit={handleSubmit(submitHandler)}>
                    <div className={styles.row}>
                        <div className={`${styles.fieldWrapper} ${styles.lockerWrapper}`}>
                            <label>
                                Property Name
                            </label>
                            <span className={styles.astrikSign}> *</span>
                            <Controller
                                name="propertyName"
                                control={control}
                                rules={{
                                    required: "Property is required",
                                }}
                                render={({ field: { value, onChange } }) => {
                                    const options = locations.map((loc) => ({
                                        value: loc.id ?? "",
                                        label: loc.name ?? "",
                                    }));

                                    return (
                                        <Select
                                            isLoading={locationLoading}
                                            isMulti={false}
                                            backspaceRemovesValue
                                            placeholder="Select Property"
                                            maxMenuHeight={130}
                                            styles={colourStyles}
                                            options={options}
                                            value={options.find((option) => option.value === value) || null}
                                            onChange={(option) => {
                                                onChange(option?.value || ""); // update RHF state
                                            }}
                                            isSearchable
                                        />
                                    );
                                }}
                            />


                            {errors?.propertyName?.message && <p className={styles.errorMsg}>{errors?.propertyName?.message}</p>}
                        </div>
                        <div className={`${styles.fieldWrapper}`}>
                            <label>
                                Event Name
                            </label>
                            <span className={styles.astrikSign}> *</span>
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: "Name is required",
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        isRequired={true}
                                        placeholder="Enter Name"
                                        {...field}

                                        onChange={(e) => {
                                            handleTrimStartChange(e, field.onChange);

                                        }}
                                    // error={errors?.vendorEmail?.message || ''}
                                    />
                                )}
                            />
                            {errors?.name && (
                                <p className={styles.errorText}>{errors?.name.message?.toString()}</p>
                            )}
                        </div>

                    </div>
                    <div className={styles.row}>
                        <div className={`${styles.fieldWrapper}`}>
                            <label>Phone Number</label>
                            <Controller
                                name="phone"
                                control={control}
                                rules={{
                                    validate: async (value) => {
                                        if (!value) return true;

                                        const digits = value?.replace(/\D/g, '') || '';
                                        const phoneDigits = digits.length > (dialCodeLength || 0) ? digits.slice(dialCodeLength || 0) : '';

                                        // If only country code is selected but no number, it's valid (optional)
                                        if (!phoneDigits) {
                                            return true;
                                        }

                                        // If there are phone digits, validate the format
                                        if (phoneDigits) {
                                            const isValid = await validatePhone(
                                                `+${value?.toString()}`,
                                                (value as any)?.countryCode as CountryCode,
                                            );
                                            if (!isValid) return 'Please enter a valid phone number';
                                        }

                                        return true;
                                    },
                                }}
                                render={({ field: { value, onChange } }) => (
                                    <PhoneInput
                                        country={'us'} // default
                                        value={value || ''}
                                        onChange={async (
                                            value: string,
                                            country: CountryData,
                                            event: React.ChangeEvent<HTMLInputElement>,
                                            formattedValue: string,
                                        ) => {
                                            const digits = value?.replace(/\D/g, '') || '';
                                            if (!digits || digits.length <= (country.dialCode?.length || 0)) {
                                                onChange(''); // no phone value
                                            } else {
                                                onChange(value);
                                            }
                                            onChange(value, country, event, formattedValue);
                                            setPhoneNumberLength(country?.format?.length);
                                            const codeLength = country?.dialCode?.length || 0;
                                            setDialCodeLength(codeLength); // <- store for validation

                                        }}
                                        inputStyle={{
                                            marginLeft: '30px',
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            width: '92%',
                                            height: '48px',
                                        }}
                                        inputProps={{
                                            maxLength: phoneNumberLength,
                                            autoFocus: false,
                                        }}
                                        countryCodeEditable={false}
                                        enableLongNumbers={true}
                                        enableSearch
                                    />
                                )}
                            />
                            {errors?.phone && <p className={styles.errorText}>{errors?.phone.message?.toString()}</p>}
                        </div>
                        <div className={`${styles.fieldWrapper}`}>
                            <label>Email</label>
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    validate: (value) => {
                                        // If no value provided, it's optional - return true
                                        if (!value) return true;

                                        // If value exists, validate email format
                                        if (value && !REGEX.EMAIL.test(value)) {
                                            return 'Enter a valid email address';
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        isRequired={false}
                                        placeholder="Enter Email"
                                        {...field}

                                        onChange={(e) => {
                                            handleTrimStartChange(e, field.onChange);
                                        }}
                                    // error={errors?.vendorEmail?.message || ''}
                                    />
                                )}
                            />
                            {errors?.email && (
                                <p className={styles.errorText}>{errors?.email.message?.toString()}</p>
                            )}
                        </div>

                    </div>
                    {/* Start Date and End Date */}
                    <div className={styles.row}>
                        <div className={styles.fieldWrapper}>
                            <div className={styles.datestyling}>
                                <label>
                                    Start Date
                                    <span className={styles.astrikSign}> *</span>
                                </label>
                                <div className={`${styles.dateInputWrapper} datePicker-wrapper`}>
                                    <Controller
                                        name="startDate"
                                        control={control}
                                        rules={{ required: 'Start date is required' }}
                                        render={({ field }) => (
                                            <DatePicker
                                                selected={eventDateToDate(field.value)}
                                                onChange={(date) => {
                                                    field.onChange(date ? moment(date).format(DISPLAY_DATE_FORMAT) : null);
                                                    if (date) {
                                                        trigger('startDate');
                                                        if (watch('endDate')) trigger('endDate');
                                                        if (watch('startTime')) trigger('startTime');
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key.toLowerCase() === 'delete' || e.key.toLowerCase() === 'backspace') {
                                                        field.onChange(null);
                                                    }
                                                    e.preventDefault()
                                                }}
                                                dateFormat="MM-dd-yyyy"
                                                placeholderText="Select Start Date"
                                                minDate={new Date()}
                                                // className={`${styles.datePickerInput} ${errors.startDate ? styles.errorInput : ''}`}
                                                className={`${styles.customDatePicker} custom-dateInput`}
                                            />
                                        )}
                                    />
                                    {errors?.startDate?.message && <p className={styles.errorMsg}>{errors?.startDate?.message}</p>}
                                </div>
                            </div>
                        </div>
                        <div className={styles.fieldWrapper}>
                            <div className={styles.datestyling}>
                                <label>
                                    End Date
                                    <span className={styles.astrikSign}> *</span>
                                </label>
                                <div className={`${styles.dateInputWrapper} datePicker-wrapper`}>
                                    <Controller
                                        name="endDate"
                                        control={control}
                                        rules={{
                                            required: 'End date is required',
                                            validate: {
                                                isAfterOrSame: (value) => {
                                                    const startDate = watch('startDate');
                                                    if (!startDate || !value) return true;

                                                    const start = parseEventDate(startDate);
                                                    const end = parseEventDate(value);
                                                    if (!start || !end) return true;

                                                    return end.isSameOrAfter(start, 'day') || 'End date must be same or after start date';
                                                },
                                            },
                                        }}
                                        render={({ field }) => {
                                            const startDate = watch('startDate');

                                            // ✅ Minimum end date = startDate (if selected) or today
                                            const minDate = eventDateToDate(startDate) || new Date();

                                            const endDateValue =
                                                eventDateToDate(field.value);

                                            return (
                                                <DatePicker
                                                    selected={endDateValue}
                                                    onChange={(date) => {
                                                        if (!date) return;
                                                        const formattedDate = moment(date).format(DISPLAY_DATE_FORMAT);
                                                        setValue('endDate', formattedDate, { shouldValidate: true });
                                                        trigger('endDate');
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key.toLowerCase() === 'delete' || e.key.toLowerCase() === 'backspace') {
                                                            field.onChange(null);
                                                        }
                                                        e.preventDefault()
                                                    }}
                                                    minDate={minDate} // ✅ ensures endDate >= startDate or today
                                                    dateFormat="MM-dd-yyyy"
                                                    placeholderText="Select End Date"
                                                    // className={`${styles.datePickerInput} ${errors.endDate ? styles.errorInput : ''}`}
                                                    className={`${styles.customDatePicker} custom-dateInput`}
                                                />
                                            );
                                        }}
                                    />

                                    {errors?.endDate?.message && <p className={styles.errorMsg}>{errors.endDate.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Start Time and End Time - Accordion Effect */}
                    <div className={`${styles.row} ${styles.timeFieldsSection}`}>
                        <div className={styles.fieldWrapper}>
                            <div className={styles.datestyling}>
                                <label>Start Time<span className={styles.astrikSign}> *</span>
                                </label>
                                <div className={`${styles.dateInputWrapper} datePicker-wrapper`}>
                                    <Controller
                                        name="startTime"
                                        control={control}
                                        rules={{
                                            required: 'Start time is required',
                                            validate: (value) => {
                                                if (!value) return 'Start time is required';

                                                const selectedDate = parseEventDate(getValues("startDate"));
                                                const today = moment();

                                                // 🟢 Ensure future time if startDate is today
                                                if (selectedDate?.isSame(today, "day")) {
                                                    const selectedTime = moment(value, "hh:mm a");
                                                    if (selectedTime.isSameOrBefore(today, "minute")) {
                                                        return "Start time must be in the future";
                                                    }
                                                }

                                                // Validate based on allowEntireTimeBlock
                                                if (startDate && endDate) {
                                                    const startDateMoment = parseEventDate(startDate);
                                                    const endDateMoment = parseEventDate(endDate);
                                                    if (!startDateMoment || !endDateMoment) return true;
                                                    const isSameDay = startDateMoment.isSame(endDateMoment, 'day');

                                                    if (isSameDay && endTime) {
                                                        const startTimeMoment = moment(value, 'hh:mm a');
                                                        const endTimeMoment = moment(endTime, 'hh:mm a');
                                                        const diffInMinutes = endTimeMoment.diff(startTimeMoment, 'minutes');
                                                        if (diffInMinutes < 60) {
                                                            return 'Time difference must be at least 1 hour for same day';
                                                        }
                                                    }
                                                }

                                                // Validate based on allowStratEndTimeEachDay
                                                // if (allowStratEndTimeEachDay && endTime) {
                                                //     const startTimeMoment = moment(value, 'hh:mm a');
                                                //     const endTimeMoment = moment(endTime, 'hh:mm a');
                                                //     const diffInMinutes = endTimeMoment.diff(startTimeMoment, 'minutes');
                                                //     if (diffInMinutes < 60) {
                                                //         return 'Time difference must be at least 1 hour';
                                                //     }
                                                // }

                                                return true;
                                            }
                                        }}
                                        render={({ field }) => {
                                            return (
                                                <DatePicker
                                                    selected={field.value ? moment(field.value, 'hh:mm a').toDate() : null}
                                                    onChange={(date) => {
                                                        if (date) {
                                                            field.onChange(moment(date).format('hh:mm a'));
                                                            trigger('startTime');
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key.toLowerCase() === 'delete' || e.key.toLowerCase() === 'backspace') {
                                                            field.onChange(null);
                                                        }
                                                        e.preventDefault()
                                                    }}
                                                    showTimeSelect
                                                    showTimeSelectOnly
                                                    timeIntervals={60}
                                                    timeCaption="Time"
                                                    dateFormat="hh:mm a"
                                                    placeholderText="Select Start Time"
                                                    className={`${styles.customDatePicker} custom-dateInput`}
                                                    minTime={
                                                        watch('startDate') &&
                                                            parseEventDate(watch('startDate'))?.isSame(moment(), 'day')
                                                            ? moment().startOf('hour').add(1, 'hour').toDate() // ✅ prevent past today
                                                            : new Date(0, 0, 0, 0, 0)
                                                    }
                                                    maxTime={new Date(0, 0, 0, 23, 30)}
                                                    filterTime={filterTimeSlots}
                                                    excludeTimes={[]}
                                                />
                                            );
                                        }}
                                    />
                                    {errors?.startTime?.message && <p className={styles.errorMsg}>{errors?.startTime?.message}</p>}
                                </div>
                            </div>
                        </div>
                        <div className={styles.fieldWrapper}>
                            <div className={styles.datestyling}>
                                <label>End Time<span className={styles.astrikSign}> *</span></label>
                                <div className={`${styles.dateInputWrapper} datePicker-wrapper`}>
                                    <Controller
                                        name="endTime"
                                        control={control}
                                        rules={{
                                            required: 'End time is required',
                                            validate: (value) => {
                                                if (!value) return 'End time is required';

                                                // Only validate time difference if dates are the same
                                                if (startDate && endDate) {
                                                    const startDateMoment = parseEventDate(startDate);
                                                    const endDateMoment = parseEventDate(endDate);
                                                    if (!startDateMoment || !endDateMoment) return true;
                                                    const isSameDay = startDateMoment.isSame(endDateMoment, 'day');

                                                    // Only check 1 hour difference for same day events
                                                    if (isSameDay && startTime) {
                                                        const startTimeMoment = moment(startTime, 'hh:mm a');
                                                        const endTimeMoment = moment(value, 'hh:mm a');
                                                        const diffInMinutes = endTimeMoment.diff(startTimeMoment, 'minutes');
                                                        if (diffInMinutes < 60) {
                                                            return 'Time difference must be at least 1 hour for same day';
                                                        }
                                                    }
                                                    // If different days, no time difference validation needed
                                                }

                                                return true;
                                            }
                                        }}
                                        render={({ field }) => {
                                            return (
                                                <DatePicker
                                                    selected={field.value ? moment(field.value, 'hh:mm a').toDate() : null}
                                                    onChange={(date) => {
                                                        if (date) {
                                                            setValue('endTime', moment(date).format('hh:mm a'), { shouldValidate: true });
                                                            trigger('endTime');
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key.toLowerCase() === 'delete' || e.key.toLowerCase() === 'backspace') {
                                                            field.onChange(null);
                                                        }
                                                        e.preventDefault()
                                                    }}
                                                    className={`${styles.customDatePicker} custom-dateInput`}
                                                    showTimeSelect
                                                    showTimeSelectOnly
                                                    timeIntervals={60}
                                                    timeCaption="Time"
                                                    dateFormat="hh:mm a"
                                                    placeholderText="Select End Time"
                                                    minTime={
                                                        startDate &&
                                                            startTime &&
                                                            endDate &&
                                                            parseEventDate(endDate)?.isSame(parseEventDate(startDate), 'day')
                                                            ? moment(`${formatEventDateForApi(startDate)} ${startTime}`, `${API_DATE_FORMAT} hh:mm a`)
                                                                .add(1, 'hour')
                                                                .toDate() // ✅ after StartTime for same day
                                                            : (endDate && parseEventDate(endDate)?.isSame(moment(), 'day')
                                                                ? moment().toDate() // ✅ prevent past times if end date is today
                                                                : new Date(0, 0, 0, 0, 0)) // ✅ allow past times if end date is future
                                                    }
                                                    maxTime={new Date(0, 0, 0, 23, 30)}
                                                    filterTime={filterEndTimeSlots}
                                                    excludeTimes={[]}
                                                />
                                            );
                                        }}
                                    />
                                    {errors?.endTime?.message && <p className={styles.errorMsg}>{errors.endTime.message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div className={`${styles.fieldWrapper} ${styles.lockerWrapper}`}>
                            <label>Select Locker</label>
                            <span className={styles.astrikSign}> *</span>
                            <div className={styles.fieldWrapperValue}>
                                <Controller
                                    name="locker"
                                    control={control}
                                    rules={{
                                        required: 'Please select a locker',
                                    }}
                                    render={({ field }) => {
                                        const options = (availableLockers || [])
                                            .map((lockerId) => ({
                                                label: `Locker ${lockerId}`,
                                                value: String(lockerId),
                                            }))
                                            .sort((a, b) => Number(a.value) - Number(b.value)); // Sorting by numeric value of 'lockerId'
                                        const handleLockerDropdownClick = () => {
                                            const missingFields = [];
                                            if (!watch('propertyName')) missingFields.push('Location');
                                            if (!startDate) missingFields.push('Start Date');
                                            if (!startTime) missingFields.push('Start Time');
                                            if (!endDate) missingFields.push('End Date');
                                            if (!endTime) missingFields.push('End Time');

                                            if (missingFields.length > 0) {
                                                setMissingFieldsMessage(`Please select ${missingFields.join(', ')} to get available lockers`);
                                                return false; // Prevent dropdown from opening
                                            }
                                            return true;
                                        };

                                        return (
                                            <Select
                                                className={styles.lockerSelect}
                                                placeholder="Select Locker"
                                                isLoading={isLockerLoading}
                                                options={availableLockers}
                                                isMulti={false}
                                                value={availableLockers.find((option: LockerOption) => option.value === field.value) || null}
                                                onChange={(selected) => field.onChange(selected ? selected.value : null)}
                                                onMenuOpen={handleLockerDropdownClick}
                                                styles={colourStyles}
                                                classNamePrefix="custom-react-select"
                                                maxMenuHeight={180}
                                            />
                                        );
                                    }}
                                />
                                {errors?.locker?.message && <p className={styles.errorMsg}>{errors?.locker?.message}</p>}
                            </div>
                        </div>
                        <div className={`${styles.fieldWrapper} ${styles.lockerWrapper}`}>
                            <label>Event Type</label>
                            <span className={styles.astrikSign}> *</span>
                            <Controller
                                name="eventType"
                                control={control}
                                rules={{
                                    required: "Event type is required",
                                }}
                                render={({ field: { value, onChange } }) => {
                                    // Convert enum to options for react-select with custom formatOptionLabel
                                    const options = Object.values(EventTypes).map((eventName) => ({
                                        value: eventName,
                                        label: eventName,
                                    }));
                                    // Custom option renderer for dropdown
                                    const formatOptionLabel = ({ value, label }: { value: string; label: string }) => {
                                        const details = EventTypeDetails[value as EventTypes];
                                        return (
                                            <div className={styles.eventTypeOption}>
                                                <div className={styles.eventTypeTitle}>{details.title}</div>
                                                <div className={styles.eventTypeDescription}>{details.description}</div>
                                            </div>
                                        );
                                    };

                                    // Get selected option details for SingleValue
                                    const getSelectedDisplay = (selectedValue: string) => {
                                        if (!selectedValue) return '';
                                        const details = EventTypeDetails[selectedValue as EventTypes];
                                        return details ? details.title : selectedValue;
                                    };

                                    return (
                                        <Select
                                            // isLoading={locationLoading}
                                            isMulti={false}
                                            backspaceRemovesValue
                                            placeholder="Select Event"
                                            maxMenuHeight={170}
                                            styles={colourStyles as any}
                                            options={options}
                                            value={options.find((option) => option.value === value) || null}
                                            onChange={(selected) => onChange(selected ? selected.value : "")}
                                            formatOptionLabel={formatOptionLabel}
                                            components={{
                                                SingleValue: ({ data }: any) => (
                                                    <div style={{ padding: '0', lineHeight: '1.5', position: 'absolute', top: '0', left: '0', height: '100%', width: '100%', display: 'flex', alignItems: 'center', paddingLeft: '14px', paddingRight: '14px', paddingTop: '10px', paddingBottom: '10px' }}>
                                                        <span style={{ fontWeight: 500, fontSize: '14px', color: '#191c21' }}>
                                                            {getSelectedDisplay(data.value)}
                                                        </span>
                                                    </div>
                                                ),
                                            }} />
                                    );
                                }} />
                            {errors?.eventType?.message && (
                                <p className={styles.errorMsg}>{errors.eventType.message}</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.fieldWrapperNoteName}>
                        <Controller
                            name="specialNotes"
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    label="Special Notes"
                                    placeholder="Enter notes"
                                    {...field}
                                    onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.btnUploadContainer}>
                        <button type="button" className={`${styles.btnUpload} ${styles.reset}`} onClick={closeModal}>
                            Cancel
                        </button>
                        <button type="submit" className={`${styles.btnUpload} ${styles.success}`} disabled={!errors}>
                            {isEditMode ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>

            {missingFieldsMessage && (
                <div style={{
                    position: 'fixed',
                    top: '120px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    zIndex: 1000,
                    transition: 'opacity 0.3s ease',
                    textAlign: 'center',
                    maxWidth: '300px',
                    width: '100%',
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                        <p>{missingFieldsMessage}</p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AddEvent
